import { useEffect, useMemo, useRef, useState } from "react"
import { Camera } from "lucide-react"
import { AssetVersionsBadge } from "@/components/media/asset-media-overlay"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { ReferenceStrip } from "@/components/references/reference-strip"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Button } from "@/components/ui/primitives"
import { EntityNameEditDialog } from "@/components/workspace/entity-name-edit-dialog"
import { useTranslation } from "@/hooks/use-translation"
import {
  createRoomViewerEngine,
  ROOM_VIEWER_PRESETS,
  WALL_IDS,
  type RoomShape,
  type RoomViewerCameraState,
  type RoomViewerEngine,
  type RoomViewerHudState,
  type WallId,
  type WallScreenAnchor,
} from "@/lib/location-room-viewer/engine"
import type { LocationCardinalFacing, LocationViewpointRow } from "@/lib/locations-api"
import { cn } from "@/lib/utils"

export const WALL_TO_FACING: Record<WallId, LocationCardinalFacing> = {
  n: "north",
  e: "east",
  s: "south",
  w: "west",
}

export type LocationRoomViewerProps = {
  wallUrls: Partial<Record<LocationCardinalFacing, string | null>>
  wallBusy: Partial<Record<LocationCardinalFacing, boolean>>
  onGenerateWall: (facing: LocationCardinalFacing) => void
  wallGenerateBlockedReason?: (facing: LocationCardinalFacing) => string | null
  /** Stable SPA reconnect id per wall facing. */
  confirmDelayKeyForWall: (facing: LocationCardinalFacing) => string
  panoramaUrl?: string | null
  panoramaBusy?: boolean
  onGeneratePanorama?: () => void
  panoramaGenerateBlockedReason?: string | null
  /** Stable SPA reconnect id for panorama generate/regenerate. */
  confirmDelayKeyForPanorama?: string
  panoramaVersionCount?: number
  onOpenPanoramaVersions?: () => void
  modelLabel?: string | null
  viewpoints?: LocationViewpointRow[]
  onCreateViewpoint?: (blob: Blob, cameraMeta: RoomViewerCameraState) => void | Promise<void>
  onRemoveViewpoint?: (viewpointId: number) => void | Promise<void>
  onRenameViewpoint?: (viewpointId: number, name: string) => void | Promise<void>
  creatingViewpoint?: boolean
  className?: string
}

const EMPTY_ANCHORS: Record<WallId, WallScreenAnchor | null> = {
  n: null,
  e: null,
  s: null,
  w: null,
}

export function LocationRoomViewer({
  wallUrls,
  wallBusy,
  onGenerateWall,
  wallGenerateBlockedReason,
  confirmDelayKeyForWall,
  panoramaUrl = null,
  panoramaBusy = false,
  onGeneratePanorama,
  panoramaGenerateBlockedReason = null,
  confirmDelayKeyForPanorama,
  panoramaVersionCount = 0,
  onOpenPanoramaVersions,
  modelLabel,
  viewpoints = [],
  onCreateViewpoint,
  onRemoveViewpoint,
  onRenameViewpoint,
  creatingViewpoint = false,
  className,
}: LocationRoomViewerProps) {
  const { t } = useTranslation(["workspace", "common"])
  const stageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const minimapRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<RoomViewerEngine | null>(null)
  const anchorsRef = useRef(EMPTY_ANCHORS)
  const hudRef = useRef<HTMLDivElement>(null)
  const wallOverlayRefs = useRef<Partial<Record<WallId, HTMLDivElement | null>>>({})

  const [shape, setShape] = useState<RoomShape>("cube")
  const [fov, setFov] = useState(60)
  const [height, setHeight] = useState(4.5)
  const [dragging, setDragging] = useState(false)
  const [renameViewpoint, setRenameViewpoint] = useState<LocationViewpointRow | null>(null)

  const wallLabels = useMemo(
    () =>
      ({
        n: t("locationsPage.facing.north"),
        e: t("locationsPage.facing.east"),
        s: t("locationsPage.facing.south"),
        w: t("locationsPage.facing.west"),
      }) satisfies Record<WallId, string>,
    [t],
  )

  const viewpointStripItems = useMemo(
    () =>
      viewpoints.map((vp, index) => ({
        key: String(vp.id),
        url: vp.url,
        thumbUrl: vp.thumb_url ?? vp.url,
        mediaType: "image" as const,
        label: vp.name?.trim() || t("locationsPage.angles.additionalItemIndexed", { index: index + 1 }),
      })),
    [viewpoints, t],
  )

  const hasViewMedia =
    shape === "sphere"
      ? Boolean(panoramaUrl)
      : Boolean(wallUrls.north || wallUrls.east || wallUrls.south || wallUrls.west)

  const createViewpointBlockedReason = !hasViewMedia
    ? t("locationsPage.roomViewer.createViewpointNeedImages")
    : undefined

  const formatHudRef = useRef<(state: RoomViewerHudState) => string>(() => "")
  formatHudRef.current = (state) =>
    t("locationsPage.roomViewer.hud", {
      x: state.x,
      y: state.y,
      z: state.z,
      yaw: state.yawDeg,
      fov: state.fov,
    })

  useEffect(() => {
    const stage = stageRef.current
    const canvas = canvasRef.current
    const minimap = minimapRef.current
    if (!stage || !canvas || !minimap) return

    const engine = createRoomViewerEngine({
      canvas,
      stage,
      minimap,
      // HUD обновляется каждый кадр (~60/с) — пишем прямо в DOM, чтобы не гонять ре-рендер React.
      onHudUpdate: (state) => {
        if (hudRef.current) hudRef.current.textContent = formatHudRef.current(state)
      },
      onHeightChange: (y) => {
        const next = Number(y.toFixed(1))
        setHeight((prev) => (prev === next ? prev : next))
      },
      onFovChange: (v) => {
        const next = Math.round(v)
        setFov((prev) => (prev === next ? prev : next))
      },
      // Wall overlays: position via DOM refs (~60fps), not React state.
      onWallAnchorsUpdate: (next) => {
        const prev = anchorsRef.current
        let changed = false
        for (const id of WALL_IDS) {
          const a = next[id]
          const b = prev[id]
          if ((a == null) !== (b == null)) {
            changed = true
            break
          }
          if (a && b && (Math.abs(a.x - b.x) > 0.5 || Math.abs(a.y - b.y) > 0.5)) {
            changed = true
            break
          }
        }
        if (!changed) return
        anchorsRef.current = next
        for (const id of WALL_IDS) {
          const el = wallOverlayRefs.current[id]
          if (!el) continue
          const anchor = next[id]
          if (!anchor) {
            el.style.display = "none"
            continue
          }
          el.style.display = ""
          el.style.left = `${anchor.x}px`
          el.style.top = `${anchor.y}px`
        }
      },
    })
    engineRef.current = engine

    const onPointerDown = () => setDragging(true)
    const onPointerUp = () => setDragging(false)
    canvas.addEventListener("pointerdown", onPointerDown)
    canvas.addEventListener("pointerup", onPointerUp)
    canvas.addEventListener("pointercancel", onPointerUp)

    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown)
      canvas.removeEventListener("pointerup", onPointerUp)
      canvas.removeEventListener("pointercancel", onPointerUp)
      engine.destroy()
      engineRef.current = null
    }
  }, [])

  useEffect(() => {
    const engine = engineRef.current
    if (!engine) return
    for (const id of WALL_IDS) {
      const facing = WALL_TO_FACING[id]
      const url = wallUrls[facing] ?? null
      void engine.setWallImage(id, url)
    }
    // Depend on stable per-facing URL strings — parent often rebuilds `wallUrls` object
    // identity on every Inertia jobs reload even when the URLs themselves are unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: compare URL values, not object identity
  }, [wallUrls.north, wallUrls.east, wallUrls.south, wallUrls.west])

  useEffect(() => {
    const engine = engineRef.current
    if (!engine) return
    void engine.setSpherePanorama(panoramaUrl)
  }, [panoramaUrl])

  const handleShape = (next: RoomShape) => {
    setShape(next)
    engineRef.current?.setShape(next)
  }

  const handleCreateViewpoint = async () => {
    const engine = engineRef.current
    if (!engine || !onCreateViewpoint || creatingViewpoint || !hasViewMedia) return
    const cameraMeta = engine.getCameraState()
    const blob = await engine.captureFrame()
    await onCreateViewpoint(blob, cameraMeta)
  }

  const handleApplyViewpoint = (index: number) => {
    const vp = viewpoints[index]
    const engine = engineRef.current
    if (!vp || !engine) return
    const meta = vp.camera_meta
    engine.applyCameraState({
      pos: meta.pos,
      yaw: meta.yaw,
      pitch: meta.pitch,
      fov: meta.fov,
      shape: meta.shape,
    })
    setShape(meta.shape)
    setFov(Math.round(meta.fov))
    setHeight(Number(meta.pos[1].toFixed(1)))
  }

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto", className)}>
      <div
        ref={stageRef}
        className="relative flex min-h-[min(52vh,28rem)] min-w-0 flex-1 items-center justify-center overflow-hidden rounded-lg bg-black"
      >
        <div className="relative">
          <canvas
            ref={canvasRef}
            tabIndex={0}
            className={cn("block outline-none", dragging ? "cursor-grabbing" : "cursor-grab")}
            style={{ touchAction: "none" }}
          />
          <div
            className="pointer-events-none absolute top-1/2 left-1/2 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 opacity-55"
            aria-hidden
          >
            <span className="absolute top-0 left-1/2 h-full w-px -translate-x-1/2 bg-ui-text-primary" />
            <span className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-ui-text-primary" />
          </div>
          {(shape === "sphere" ? (["n", "s"] as const) : WALL_IDS).map((id) => {
            const facing = WALL_TO_FACING[id]
            const url = wallUrls[facing] ?? null
            const hasImage = Boolean(url)
            // Sphere: both N/S overlays are one panorama action — shared busy/blocked so they lock/unlock together.
            const busy = shape === "sphere" ? panoramaBusy : Boolean(wallBusy[facing])
            const blockedReason =
              shape === "sphere"
                ? (panoramaGenerateBlockedReason ?? null)
                : (wallGenerateBlockedReason?.(facing) ?? null)
            const label =
              shape === "sphere"
                ? panoramaUrl
                  ? t("locationsPage.roomViewer.regeneratePanorama")
                  : t("locationsPage.roomViewer.generatePanorama")
                : hasImage
                  ? t("locationsPage.regenerateView", { view: wallLabels[id] })
                  : t("locationsPage.generateView", { view: wallLabels[id] })
            const disabled = shape === "sphere" ? !onGeneratePanorama || Boolean(blockedReason) : Boolean(blockedReason)
            return (
              <div
                key={id}
                ref={(el) => {
                  wallOverlayRefs.current[id] = el
                }}
                className="pointer-events-auto absolute z-20 -translate-x-1/2 -translate-y-1/2"
                style={{ left: 0, top: 0, display: "none" }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col items-center gap-1">
                  <ActionHintTooltip label={blockedReason ?? undefined}>
                    <GenerateWithModelLabel
                      label={label}
                      busyLabel={t("locationsPage.queuedShort")}
                      modelLabel={modelLabel}
                      busy={busy}
                      disabled={disabled}
                      disabledReason={blockedReason ?? undefined}
                      stopPropagation
                      buttonClassName="shadow-lg"
                      confirmDelayKey={
                        shape === "sphere"
                          ? (confirmDelayKeyForPanorama ?? confirmDelayKeyForWall(facing))
                          : confirmDelayKeyForWall(facing)
                      }
                      onClick={() => {
                        if (shape === "sphere") {
                          onGeneratePanorama?.()
                          return
                        }
                        onGenerateWall(facing)
                      }}
                    />
                  </ActionHintTooltip>
                  {blockedReason ? (
                    <p className="max-w-[14rem] text-center text-[10px] leading-snug text-ui-text-muted drop-shadow-sm">
                      {blockedReason}
                    </p>
                  ) : null}
                </div>
              </div>
            )
          })}
          <div
            className="absolute bottom-2.5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <ActionHintTooltip label={createViewpointBlockedReason}>
              <Button
                type="button"
                size="sm"
                className="gap-1.5 shadow-lg"
                disabled={!onCreateViewpoint || creatingViewpoint || !hasViewMedia}
                onClick={() => void handleCreateViewpoint()}
              >
                <Camera className="size-3.5" aria-hidden />
                {creatingViewpoint
                  ? t("locationsPage.roomViewer.creatingViewpoint")
                  : t("locationsPage.roomViewer.createViewpoint")}
              </Button>
            </ActionHintTooltip>
          </div>
        </div>

        <div
          ref={hudRef}
          className="pointer-events-none absolute bottom-2.5 left-2.5 z-10 rounded-md bg-black/70 px-2 py-1 font-mono text-[11px] whitespace-pre text-ui-text-muted"
        />

        {shape === "sphere" && onOpenPanoramaVersions ? (
          <div className="absolute top-2.5 left-2.5 z-30" onPointerDown={(e) => e.stopPropagation()}>
            <AssetVersionsBadge
              count={panoramaVersionCount}
              onOpen={onOpenPanoramaVersions}
              title={t("locationsPage.versions")}
              className="shadow-lg"
            />
          </div>
        ) : null}

        <div
          className="absolute top-2.5 right-2.5 z-30 flex w-[14rem] flex-col items-stretch gap-1.5"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div
            role="group"
            aria-label={t("locationsPage.roomViewer.shape")}
            className="flex gap-1 rounded-lg border border-ui-border bg-black/80 p-0.5"
          >
            <button
              type="button"
              className={cn(
                "flex-1 rounded-md px-2 py-1 text-xs",
                shape === "cube" ? "bg-ui-accent font-semibold text-ui-on-accent" : "text-white/55 hover:text-white",
              )}
              onClick={() => handleShape("cube")}
            >
              {t("locationsPage.roomViewer.shapeCube")}
            </button>
            <button
              type="button"
              className={cn(
                "flex-1 rounded-md px-2 py-1 text-xs",
                shape === "cylinder"
                  ? "bg-ui-accent font-semibold text-ui-on-accent"
                  : "text-white/55 hover:text-white",
              )}
              onClick={() => handleShape("cylinder")}
            >
              {t("locationsPage.roomViewer.shapeCylinder")}
            </button>
            <button
              type="button"
              className={cn(
                "flex-1 rounded-md px-2 py-1 text-xs",
                shape === "sphere" ? "bg-ui-accent font-semibold text-ui-on-accent" : "text-white/55 hover:text-white",
              )}
              onClick={() => handleShape("sphere")}
            >
              {t("locationsPage.roomViewer.shapeSphere")}
            </button>
          </div>
          <div className="flex w-full gap-2 rounded-lg border border-ui-border bg-black/80 p-2">
            {shape !== "sphere" ? (
              <div className="flex h-[150px] items-center gap-1">
                <span className="w-6 shrink-0 text-right font-mono text-[10px] text-ui-text-muted">
                  {height.toFixed(1)}
                </span>
                <div className="relative flex h-[150px] w-5 items-center justify-center">
                  <input
                    type="range"
                    min={0.6}
                    max={8.4}
                    step={0.1}
                    value={height}
                    aria-label={t("locationsPage.roomViewer.height")}
                    className="h-5 w-[150px] origin-center -rotate-90 accent-[var(--ui-accent)]"
                    onChange={(e) => {
                      const v = Number(e.target.value)
                      setHeight(v)
                      engineRef.current?.setHeight(v)
                    }}
                  />
                </div>
              </div>
            ) : null}
            <div
              className={cn("flex min-w-0 flex-col gap-1", shape === "sphere" ? "w-full flex-1" : "w-[150px] shrink-0")}
            >
              {/* Always mounted for engine; hidden in sphere so minimap square is not shown. */}
              <canvas
                ref={minimapRef}
                width={150}
                height={150}
                className={cn("block cursor-crosshair rounded", shape === "sphere" && "hidden")}
                aria-hidden={shape === "sphere"}
              />
              <div className="flex w-full items-center gap-1">
                <input
                  type="range"
                  min={25}
                  max={100}
                  value={fov}
                  aria-label={t("locationsPage.roomViewer.fov")}
                  className="min-w-0 flex-1 accent-[var(--ui-accent)]"
                  onChange={(e) => {
                    const v = Number(e.target.value)
                    setFov(v)
                    engineRef.current?.setFov(v)
                  }}
                />
                <span className="w-8 shrink-0 text-right font-mono text-[10px] text-ui-text-muted">{fov}°</span>
              </div>
              {shape !== "sphere" ? (
                <p className="text-center font-mono text-[8px] text-ui-text-muted">
                  {t("locationsPage.roomViewer.minimapHint")}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <aside className="flex w-full shrink-0 flex-col gap-3 overflow-x-auto sm:flex-row sm:items-stretch">
        <section
          className="min-w-0 shrink-0 rounded-lg border border-ui-border bg-ui-surface-raised p-3 sm:w-[17.5rem]"
          aria-label={t("locationsPage.roomViewer.cameraTitle")}
        >
          <div className="flex flex-wrap gap-1.5">
            {ROOM_VIEWER_PRESETS.map((p, i) => {
              const presetLabel =
                p.labelKey === "center"
                  ? t("locationsPage.roomViewer.preset.center")
                  : p.labelKey === "nw"
                    ? t("locationsPage.roomViewer.preset.nw")
                    : p.labelKey === "ne"
                      ? t("locationsPage.roomViewer.preset.ne")
                      : p.labelKey === "sw"
                        ? t("locationsPage.roomViewer.preset.sw")
                        : t("locationsPage.roomViewer.preset.se")
              return (
                <button
                  key={p.labelKey}
                  type="button"
                  className="rounded-md border border-ui-border bg-ui-bg-elevated px-2.5 py-1 text-xs text-ui-text-primary hover:border-ui-text-muted"
                  onClick={() => engineRef.current?.goToPreset(i)}
                >
                  {presetLabel}
                </button>
              )
            })}
          </div>
        </section>

        <section
          className="min-w-0 flex-1 overflow-hidden rounded-lg border border-ui-border bg-ui-surface-raised p-3"
          aria-label={t("locationsPage.roomViewer.viewpointsTitle")}
        >
          {viewpointStripItems.length > 0 ? (
            <ReferenceStrip
              layout="scroll"
              references={viewpointStripItems}
              showAddTile={false}
              onRemove={
                onRemoveViewpoint
                  ? (index) => {
                      const vp = viewpoints[index]
                      if (vp) void onRemoveViewpoint(vp.id)
                    }
                  : undefined
              }
              onApply={handleApplyViewpoint}
              applyTitle={t("locationsPage.roomViewer.applyViewpoint")}
              onLabelClick={
                onRenameViewpoint
                  ? (index) => {
                      const vp = viewpoints[index]
                      if (vp) setRenameViewpoint(vp)
                    }
                  : undefined
              }
              ariaLabel={t("locationsPage.roomViewer.viewpointsTitle")}
            />
          ) : (
            <p className="text-xs text-ui-text-muted">{t("locationsPage.roomViewer.viewpointsEmpty")}</p>
          )}
        </section>

        <p className="shrink-0 px-1 text-[11px] leading-snug whitespace-pre-line text-ui-text-muted sm:max-w-[10rem] sm:self-center">
          {shape === "sphere"
            ? t("locationsPage.roomViewer.controlsHintSphere")
            : t("locationsPage.roomViewer.controlsHint")}
        </p>
      </aside>

      {onRenameViewpoint ? (
        <EntityNameEditDialog
          open={renameViewpoint != null}
          onOpenChange={(open) => {
            if (!open) setRenameViewpoint(null)
          }}
          title={t("locationsPage.roomViewer.renameViewpointTitle")}
          nameLabel={t("locationsPage.roomViewer.renameViewpointNameLabel")}
          initialName={
            renameViewpoint
              ? renameViewpoint.name?.trim() ||
                (() => {
                  const idx = viewpoints.findIndex((vp) => vp.id === renameViewpoint.id)
                  return t("locationsPage.angles.additionalItemIndexed", {
                    index: idx >= 0 ? idx + 1 : 1,
                  })
                })()
              : ""
          }
          onSubmit={async (name) => {
            if (!renameViewpoint) return
            await onRenameViewpoint(renameViewpoint.id, name)
            setRenameViewpoint(null)
          }}
          closeAriaLabel={t("close", { ns: "common" })}
          cancelLabel={t("cancel", { ns: "common" })}
          saveLabel={t("save", { ns: "common" })}
        />
      ) : null}
    </div>
  )
}
